import Stack from "@mui/material/Stack";
import { theme } from "@polifonia/theme";
import type { ComponentProps, FC } from "react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { InfoBox } from "@/components/InfoBox";
import { MotifIcon } from "@/components/MotifIcon";
import type { InterludeTypes } from "@/interlude";

interface EmailActivationProps {
  senderDomains: Array<
    Pick<InterludeTypes["SenderDomain"], "id" | "domain" | "status">
  >;
  senderProfiles: Array<
    Pick<InterludeTypes["SenderProfile"], "id" | "fromEmail" | "status">
  >;
}

export const EmailActivation: FC<EmailActivationProps> = ({
  senderDomains,
  senderProfiles,
}) => {
  const { t } = useTranslation();

  const domainCounts = useMemo(() => {
    return senderDomains.reduce(
      (acc, domain) => {
        acc[domain.status]++;
        return acc;
      },
      { verified: 0, warning: 0, failed: 0 },
    );
  }, [senderDomains]);

  const profileCounts = useMemo(() => {
    return senderProfiles.reduce(
      (acc, profile) => {
        acc[profile.status]++;
        return acc;
      },
      { verified: 0, warning: 0, failed: 0 },
    );
  }, [senderProfiles]);

  const renderStatusItem = useCallback(
    (
      /** Translated string */
      label: string,
      counts: {
        verifiedCount: number;
        warningCount: number;
        failedCount: number;
      },
    ) => {
      const { verifiedCount, warningCount, failedCount } = counts;
      const hasError = failedCount > 0;
      const hasWarning = warningCount > 0;
      const isSuccess = !hasError && !hasWarning && verifiedCount > 0;

      let variant: ComponentProps<typeof InfoBox>["variant"] = "success";
      let iconName: string = "circle_check";
      let iconColor: string = theme.colors.staticFgPositive;

      if (hasError) {
        variant = "error";
        iconName = "circle_cross";
        iconColor = theme.colors.staticFgNegative;
      } else if (hasWarning) {
        variant = "warning";
        iconName = "warning";
        iconColor = theme.colors.staticFgAttentive;
      } else if (isSuccess) {
        variant = "success";
        iconName = "circle_check";
        iconColor = theme.colors.staticFgPositive;
      } else {
        variant = "plain";
        iconName = "circle_info";
        iconColor = theme.colors.staticFgAttentive;
      }

      return (
        <InfoBox
          variant={variant}
          icon={(() => {
            switch (iconName) {
              case "circle_check":
                return (
                  <MotifIcon
                    un-i-motif="circle_check"
                    style={{ fontSize: 16, color: iconColor }}
                  />
                );
              case "circle_cross":
                return (
                  <MotifIcon
                    un-i-motif="circle_cross"
                    style={{ fontSize: 16, color: iconColor }}
                  />
                );
              case "warning":
                return (
                  <MotifIcon
                    un-i-motif="warning"
                    style={{ fontSize: 16, color: iconColor }}
                  />
                );
              default:
                return null;
            }
          })()}
          description={label}
          transparent
        />
      );
    },
    [],
  );

  return (
    <Stack spacing="4px">
      {renderStatusItem(
        domainCounts.warning > 0 || domainCounts.failed > 0
          ? t("channels.email.activation.senderDomainStatus", {
              verifiedCount: domainCounts.verified,
              warningCount: domainCounts.warning,
              failedCount: domainCounts.failed,
            })
          : t("channels.email.activation.senderDomainStatus.onlySuccess", {
              verifiedCount: domainCounts.verified,
            }),
        {
          verifiedCount: domainCounts.verified,
          warningCount: domainCounts.warning,
          failedCount: domainCounts.failed,
        },
      )}
      {renderStatusItem(
        profileCounts.warning > 0 || profileCounts.failed > 0
          ? t("channels.email.activation.senderProfileStatus", {
              verifiedCount: profileCounts.verified,
              warningCount: profileCounts.warning,
              failedCount: profileCounts.failed,
            })
          : t("channels.email.activation.senderProfileStatus.onlySuccess", {
              verifiedCount: profileCounts.verified,
            }),
        {
          verifiedCount: profileCounts.verified,
          warningCount: profileCounts.warning,
          failedCount: profileCounts.failed,
        },
      )}
    </Stack>
  );
};
